import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/OffgridLeave.png';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#06070B] text-[#F1F5F9] font-sans antialiased flex flex-col overflow-x-hidden selection:bg-blue-500/20 selection:text-blue-300">

      {/* Navigation */}
      <nav className="border-b border-slate-900 bg-[#06070B]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between h-24 items-center">
            
            {/* Expanded Prominent Corporate Logo */}
            <div className="flex items-center">
              <img 
                src={logo} 
                alt="OffGrid Logo" 
                className="h-14 sm:h-16 w-auto object-contain object-left transition-all"
                style={{ imageRendering: '-webkit-optimize-contrast' }}
              />
            </div>
            
            {/* System Key Actions */}
            <div className="flex items-center space-x-8 font-medium text-xs tracking-wider">
              <a href="#framework" className="text-slate-400 hover:text-white uppercase transition-colors">
                System Overview
              </a>
              <Link
                to="/login"
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-2.5 rounded shadow-md shadow-blue-600/10 transition-all uppercase tracking-wider text-[11px]"
              >
                Employee Portal
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow">
        {/* Architectural Hero */}
        <section className="relative pt-24 pb-20 lg:pt-36 lg:pb-32">
          {/* Ambient Lighting Depth */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-blue-500/[0.03] rounded-full blur-[130px] -z-10" />

          <div className="max-w-4xl mx-auto px-6 text-center">
            
            {/* Security Compliance Tag */}
            <div className="inline-flex items-center gap-2 bg-[#0E111A] border border-slate-800/60 px-3 py-1 rounded font-mono text-[10px] tracking-widest text-slate-400 mb-8 uppercase">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              Standalone Workspace Environment
            </div>

            {/* High-End Enterprise Typographic Polish */}
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]">
              Automated leave tracking <br />
              <span className="font-light bg-gradient-to-r from-slate-200 via-slate-400 to-slate-600 bg-clip-text text-transparent">
                for contract & temporary cohorts.
              </span>
            </h1>

            {/* Professional HR Contextual Text */}
            <p className="mt-8 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto font-light leading-relaxed tracking-wide">
              Manage non-permanent personnel efficiently without administrative overhead. OffGrid operates as a secure, standalone human capital management system designed to provision leave balances, monitor capacity calendars, and process supervisor authorizations independently.
            </p>

            {/* Corporate Actions */}
            <div className="mt-12 flex flex-col sm:flex-row justify-center items-center gap-4 font-mono text-xs tracking-wider">
              <Link
                to="/login"
                className="w-full sm:w-auto bg-[#F8FAFC] hover:bg-[#E2E8F0] text-[#020617] font-semibold px-8 py-4 rounded transition-all text-center"
              >
                ACCESS WORKSPACE
              </Link>
              <a
                href="#framework"
                className="w-full sm:w-auto bg-[#0E111A] text-slate-400 hover:text-white border border-slate-800 px-8 py-4 rounded transition-all text-center"
              >
                SYSTEM FRAMEWORK
              </a>
            </div>

          </div>
        </section>

        {/* Feature Matrix & Live Workspace Simulation Preview */}
        <section id="framework" className="bg-[#090A0F] border-t border-slate-900 py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              
              {/* Left Column Text (Spans 2 columns on desktop) */}
              <div className="lg:col-span-2 space-y-8">
                <div className="border-l border-blue-500 pl-4">
                  <span className="text-[10px] font-mono tracking-[0.3em] text-blue-400 uppercase block mb-1">Architecture Overview</span>
                  <h2 className="text-3xl font-bold text-white tracking-tight">
                    Isolated Governance Logic.
                  </h2>
                </div>
                
                <p className="text-slate-400 font-light text-sm leading-relaxed">
                  When employees are maintained outside core infrastructure registries, monitoring balances across decentralized records causes compliance discrepancies. OffGrid solves this with comprehensive internal automation modules.
                </p>

                <div className="space-y-6">
                  {/* Miniature Feature 1 */}
                  <div className="flex gap-4">
                    <div className="w-5 h-5 rounded bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-[10px] shrink-0 font-bold">01</div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-1">Onboarding & Allocation Profiles</h4>
                      <p className="text-xs text-slate-400 font-light leading-relaxed">Define specific placement contracts, establish custom time-off thresholds, and establish immediate management linkages during initial setup.</p>
                    </div>
                  </div>

                  {/* Miniature Feature 2 */}
                  <div className="flex gap-4">
                    <div className="w-5 h-5 rounded bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-[10px] shrink-0 font-bold">02</div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-1">Real-Time Ledger Auditing</h4>
                      <p className="text-xs text-slate-400 font-light leading-relaxed">Asynchronous balance tracking dynamically updates profile allocation records instantly upon authorization adjustments.</p>
                    </div>
                  </div>

                  {/* Miniature Feature 3 */}
                  <div className="flex gap-4">
                    <div className="w-5 h-5 rounded bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-[10px] shrink-0 font-bold">03</div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-1">Centralized Supervisor Controls</h4>
                      <p className="text-xs text-slate-400 font-light leading-relaxed">Managers utilize a dedicated administrative pipeline to authorize applications, cross-reference department schedules, and document adjustments.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column Layout Preview (Spans 3 columns on desktop) */}
              <div className="lg:col-span-3 bg-[#0E111A] border border-slate-800/80 rounded-xl p-6 shadow-2xl relative overflow-hidden">
                
                {/* Decorative UI Window Controls */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-800/60 text-xs font-mono text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
                    <span className="ml-2 text-[10px] text-slate-400 font-sans tracking-wide">Manager Dashboard View</span>
                  </div>
                  <span className="text-[10px] text-slate-500">// LIVE_RECORDS</span>
                </div>

                {/* Simulated UI Content Block */}
                <div className="space-y-4 font-sans text-xs">
                  
                  {/* Simulated Row 1 */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded bg-slate-900/40 border border-slate-800/30 gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                      <span className="text-slate-300 font-medium">Alex Tan (Marketing Placement)</span>
                    </div>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className="text-slate-500">Sick Leave (2 Days)</span>
                      <span className="text-amber-400 bg-amber-500/5 border border-amber-500/20 px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider">PENDING</span>
                    </div>
                  </div>

                  {/* Simulated Row 2 */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded bg-slate-900/40 border border-slate-800/30 gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-slate-300 font-medium">John Doe (Engineering Placement)</span>
                    </div>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className="text-slate-500">Annual Leave (1 Day)</span>
                      <span className="text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider">APPROVED</span>
                    </div>
                  </div>

                  {/* Simulated Row 3 */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded bg-slate-900/40 border border-slate-800/30 gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-rose-500" />
                      <span className="text-slate-300 font-medium">Sarah Jenkins (Design Placement)</span>
                    </div>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className="text-slate-500">Casual Leave (3 Days)</span>
                      <span className="text-rose-400 bg-rose-500/5 border border-rose-500/20 px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider">REJECTED</span>
                    </div>
                  </div>

                </div>

                {/* Ambient Decorative Graphic */}
                <div className="mt-6 pt-4 border-t border-slate-800/60 flex justify-between items-center text-[10px] font-mono text-slate-500">
                  <span>ACTIVE COHORT SIZE: 14 PARTICIPANTS</span>
                  <span>DATABASE CONNECTION: SECURED</span>
                </div>

              </div>

            </div>
          </div>
        </section>
      </main>

      {/* Expanded Multi-Column HR Corporate Footer */}
      <footer className="border-t border-slate-900 bg-[#06070B] pt-16 pb-8 text-xs text-slate-400">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            
            {/* Identity Column */}
            <div className="col-span-2 md:col-span-1 space-y-4">
              <img src={logo} alt="OffGrid" className="h-10 w-auto opacity-90 object-contain object-left" />
              <p className="text-[11px] text-slate-500 leading-relaxed font-light">
                Human Resources independent tracking environment optimizing operational deployment and allocation tracking.
              </p>
            </div>

            {/* Portal Directory Links */}
            <div>
              <h5 className="text-white font-semibold mb-4 text-[11px] uppercase tracking-wider text-slate-300">Portals</h5>
              <ul className="space-y-2 text-[11px] font-light text-slate-400">
                <li><Link to="/login" className="hover:text-blue-400 transition-colors">Employee Sign In</Link></li>
                <li><Link to="/login" className="hover:text-blue-400 transition-colors">Supervisor Hub</Link></li>
                <li><Link to="/login" className="hover:text-blue-400 transition-colors">System Administrator Access</Link></li>
              </ul>
            </div>

            {/* System Support Links */}
            <div>
              <h5 className="text-white font-semibold mb-4 text-[11px] uppercase tracking-wider text-slate-300">Resources</h5>
              <ul className="space-y-2 text-[11px] font-light text-slate-400">
                <li><a href="#framework" className="hover:text-blue-400 transition-colors">Allocation Matrix Rules</a></li>
                <li><a href="#framework" className="hover:text-blue-400 transition-colors">Data Management Security</a></li>
                <li><a href="#framework" className="hover:text-blue-400 transition-colors">Internal Usage Documentation</a></li>
              </ul>
            </div>

            {/* Operational System Parameters */}
            <div className="font-mono text-[10px] text-slate-500 space-y-1 bg-[#090A0F] border border-slate-900 p-4 rounded-md h-fit">
              <div className="text-slate-400 font-semibold mb-1 uppercase text-[9px] tracking-wider font-sans">Instance Data</div>
              <div>ENVIRONMENT: SECURE ISOLATED</div>
              <div>REDUNDANCY: MULTI-REGION</div>
              <div>COMPLIANCE: COHORT LEVEL</div>
            </div>

          </div>

          {/* Legal Compliance Sub-Footer */}
          <div className="pt-8 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center gap-4 text-[11px] text-slate-500 font-light">
            <div className="text-center sm:text-left">
              &copy; {new Date().getFullYear()} OffGrid Systems. All rights reserved. Built and conceptualized by Sharleen.
            </div>
            <div className="flex space-x-6">
              <span className="hover:text-slate-400 transition-colors cursor-pointer">Internal Privacy Policy</span>
              <span className="hover:text-slate-400 transition-colors cursor-pointer">Terms of Operation</span>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default LandingPage;